import * as yup from "yup";

const ALLOWED_EXTENSIONS = [".png", ".jpg", ".jpeg", ".pdf", ".heif", ".heic"];

function isValidFileValue(value: string): boolean {
  if (value.startsWith("http")) {
    try {
      const pathname = new URL(value).pathname.toLowerCase();
      return ALLOWED_EXTENSIONS.some((ext) => pathname.endsWith(ext));
    } catch {
      return false;
    }
  }
  return (
    value.startsWith("data:image/png") ||
    value.startsWith("data:image/jpeg") ||
    value.startsWith("data:image/heif") ||
    value.startsWith("data:image/heic") ||
    value.startsWith("data:application/pdf")
  );
}

const requiredFileTypeTest = (value: string | null | undefined) => {
  if (!value) return false;
  return isValidFileValue(value);
};

const optionalFileTypeTest = (value: string | null | undefined) => {
  if (!value || value === "") return true;
  return isValidFileValue(value);
};

export const youthMembershipSchema = yup.object().shape({
  universityCollege: yup.string().required("institution is required"),
  dateOfBirth: yup.date().required("Date of Birth is required"),
  idDocument: yup
    .string()
    .required("ID Upload is required")
    .test("fileType", "Unsupported File Format", requiredFileTypeTest),
  phoneNumber: yup
    .string()
    .required("Phone Number is required")
    .min(11, "Phone number must be at least 11 characters"),
  city: yup.string().required("City is required"),
  country: yup.string().required("Country is required"),
  gender: yup.string().required("Gender is required"),
  languagePreference: yup.string().required("Language Preference is required"),
  professionalPhoto: yup
    .string()
    .nullable()
    .test("fileType", "Unsupported File Format", optionalFileTypeTest),
  aboutYou: yup.string().required("About You is required"),
  educationLevel: yup.string().required("Education Level is required"),
  studyInterest: yup
    .string()
    .required("Field of Study or Interest is required"),
  skillsExperience: yup.string().nullable(),
  mentorshipObjectives: yup
    .string()
    .required("Mentorship Objectives are required"),
  mentorshipExpertise: yup
    .string()
    .required("Preferred Mentor Expertise is required"),
  mentorshipFormat: yup.string().required("Mentorship Format is required"),
  platformInterest: yup.string().required("Platform Interests are required"),
  peerNetworking: yup
    .string()
    .required("Peer Networking selection is required"),
});

export const ageVerificationSchema = yup.object({
  dateOfBirth: yup.date().required("Date of Birth is required"),
  idDocument: yup
    .string()
    .required("ID Upload is required")
    .test("fileType", "Unsupported File Format", requiredFileTypeTest),
  phoneNumber: yup
    .string()
    .required("Phone Number is required")
    .min(11, "Phone number must be at least 11 characters"),
  city: yup.string().required("City is required"),
  country: yup.string().required("Country is required"),
});

export const personalProfileSchema = yup.object({
  gender: yup.string().required("Gender is required"),
  languagePreference: yup.string().required("Language Preference is required"),
  professionalPhoto: yup
    .string()
    .nullable()
    .test("fileType", "Unsupported File Format", optionalFileTypeTest),
  aboutYou: yup.string().required("About You is required"),
  educationLevel: yup.string().required("Education Level is required"),
  studyInterest: yup
    .string()
    .required("Field of Study or Interest is required"),
  skillsExperience: yup.string().nullable(),
  universityCollege: yup.string().required("institution is required"),
});

// Mentorship Goals
export const mentorshipGoalsSchema = yup.object({
  mentorshipObjectives: yup
    .string()
    .required("Mentorship Objectives are required"),
  mentorshipExpertise: yup
    .string()
    .required("Preferred Mentor Expertise is required"),
  mentorshipFormat: yup.string().required("Mentorship Format is required"),
  platformInterest: yup.string().required("Platform Interests are required"),
  peerNetworking: yup
    .string()
    .required("Peer Networking selection is required"),
});

export const personalInfoSchema = yup.object({
  personalGender: yup.string().required("Gender is required"),
  personalLanguagePreference: yup
    .string()
    .required("Language Preference is required"),
  personalAbout: yup.string().nullable(),

  personalProfessionalPhoto: yup
    .string()
    .optional()
    .nullable()
    .test("fileType", "Unsupported File Format", optionalFileTypeTest),

  personalPositionCompany: yup
    .string()
    .required("Position in the company is required"),
  businessName: yup.string().required("Business Name is required"),

  businessLogo: yup
    .string()
    .optional()
    .nullable()
    .test("fileType", "Unsupported File Format", optionalFileTypeTest),

  businessSectorIndustry: yup.string().required("Sector/Industry is required"),
  businessAbout: yup.string().nullable(),
  businessWebsiteUrl: yup.string().url("Invalid URL"),
  businessCountryOperation: yup
    .string()
    .required("Countries of Operation is required"),
});

export const businessContactSchema = yup.object({
  businessAddressLineOne: yup
    .string()
    .required("First Line of Address is required"),
  businessAddressLineTwo: yup.string().nullable(),
  businessCountry: yup.string().required("Country is Required"), //Updated Validation
  city: yup.string().required("City is required"),
  businessPostCode: yup.string().required("Postcode is required"),
  county: yup.string().nullable(),
  businessTelephoneNumber: yup
    .string()
    .matches(/^[0-9]+$/, "Business Telephone must be numbers only")
    .required("Business Telephone is required"),
  businessCurrency: yup.string().required("Currency is required"),
  businessTurnover: yup
    .string()
    .typeError("Turnover must be a number")
    .required("Annual Turnover is required"),
  businessObjectives: yup.string().required("Business Objectives are required"),
});

export const platformSpecificSchema = yup.object({
  businessProductServices: yup.string().nullable(),
  businessDiscount: yup.string().nullable(),
  productInterested: yup.string().required("must have selected one"),
  businessDiscountCode: yup.string().nullable(),
});

export const businessMembershipSchema = yup.object().shape({
  businessName: yup.string().required("Business Name is required"),
  businessLogo: yup
    .string()
    .optional()
    .nullable()
    .test("fileType", "Unsupported File Format", optionalFileTypeTest),

  businessSectorIndustry: yup.string().required("Sector/Industry is required"),
  businessAbout: yup.string().nullable(),
  businessWebsiteUrl: yup.string().url("Invalid URL"),
  businessCountryOperation: yup
    .string()
    .required("Countries of Operation is required"),

  businessAddressLineOne: yup
    .string()
    .required("First Line of Address is required"),
  businessAddressLineTwo: yup.string().nullable(),
  businessCountry: yup.string().required("Country is Required"), // Updated Validation
  city: yup.string().required("City is required"),
  businessPostCode: yup.string().required("Postcode is required"),
  county: yup.string().nullable(),
  businessTelephoneNumber: yup
    .string()
    .matches(/^[0-9]+$/, "Business Telephone must be numbers only")
    .required("Business Telephone is required"),

  personalGender: yup.string().required("Gender is required"),
  personalLanguagePreference: yup
    .string()
    .required("Language Preference is required"),
  personalAbout: yup.string().nullable(),
  personalProfessionalPhoto: yup
    .string()
    .optional()
    .nullable()
    .test("fileType", "Unsupported File Format", optionalFileTypeTest),
  personalPositionCompany: yup
    .string()
    .required("Position in the company is required"),

  businessCurrency: yup.string().required("Currency is required"),
  businessTurnover: yup
    .string()
    .typeError("Turnover must be a number")
    .required("Annual Turnover is required"),
  businessObjectives: yup.string().required("Business Objectives are required"),

  businessProductServices: yup.string().nullable(),
  businessDiscount: yup.string().nullable(),
  productInterested: yup.string().required("must have selected one"),
  businessDiscountCode: yup.string().nullable(),
});
